import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MagnifyingGlass, MapPin, Sparkle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const GrantDiscovery = () => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalGrants, setTotalGrants] = useState(0);
  const [filters, setFilters] = useState({
    sector: '',
    geography: '',
    funder_type: '',
    min_amount: '',
    max_amount: ''
  });
  const [discovering, setDiscovering] = useState(false);

  const fetchGrants = useCallback(async (pageNum = 1, isLoadMore = false) => {
    if (!isLoadMore) setLoading(true);
    
    // Initial fetch delay to show "fetching thing"
    if (!isLoadMore) await new Promise(resolve => setTimeout(resolve, 1500));
    
    try {
      const response = await axios.get(`${API}/grants`, {
        params: {
          page: pageNum,
          limit: 12,
          sector: filters.sector,
          geography: filters.geography,
          funder_type: filters.funder_type,
          search: searchTerm
        }
      });
      
      const newGrants = response.data.grants;
      if (isLoadMore) {
        setGrants(prev => [...prev, ...newGrants]);
      } else {
        setGrants(newGrants);
      }
      
      setTotalGrants(response.data.total);
      setHasMore(pageNum < response.data.totalPages);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch grants:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm]);

  useEffect(() => {
    fetchGrants(1, false);
  }, [fetchGrants]);

  const loadMore = () => {
    if (hasMore && !loading) {
      fetchGrants(page + 1, true);
    }
  };

  const handleAIDiscovery = async () => {
    setDiscovering(true);
    // Random delay between 4000ms and 5000ms for premium 'AI thinking' effect
    const delay = Math.floor(Math.random() * 1000) + 4000;
    await new Promise(resolve => setTimeout(resolve, delay));
    
    // Rearrange the things (shuffle)
    setGrants(prev => [...prev].sort(() => Math.random() - 0.5));
    
    setDiscovering(false);
  };

  const SkeletonCard = () => (
    <div className="bg-card border border-border rounded-md p-6 h-[350px] animate-pulse">
      <div className="flex justify-between mb-4">
        <div className="w-20 h-6 bg-muted rounded"></div>
        <div className="w-12 h-6 bg-muted rounded"></div>
      </div>
      <div className="w-3/4 h-8 bg-muted rounded mb-4"></div>
      <div className="w-1/2 h-4 bg-muted rounded mb-4"></div>
      <div className="flex gap-2 mb-4">
        <div className="w-16 h-4 bg-muted rounded"></div>
        <div className="w-16 h-4 bg-muted rounded"></div>
      </div>
      <div className="flex-1 space-y-2 mb-4">
        <div className="w-full h-4 bg-muted rounded"></div>
        <div className="w-5/6 h-4 bg-muted rounded"></div>
      </div>
      <div className="mt-auto">
        <div className="w-24 h-8 bg-muted rounded mb-2"></div>
        <div className="w-full h-10 bg-muted rounded"></div>
      </div>
    </div>
  );

  const sectors = ["Education", "Healthcare", "Environment", "Human Rights", "LGBTQ+ Rights", "Women Empowerment", "Youth Development"];
  const geographies = ["California", "National", "New York", "Texas", "Illinois", "Global"];

  return (
    <div className="h-full overflow-auto p-6 space-y-6 relative bg-background text-foreground transition-colors duration-300">
      {discovering && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-md">
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 bg-[#E04E39]/10 rounded-full">
              <Sparkle size={48} weight="fill" className="text-[#E04E39] animate-pulse" />
            </div>
            <div className="text-2xl font-bold tracking-tight">AI Analyzing Opportunities...</div>
            <div className="w-64 h-1.5 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#E04E39] shadow-[0_0_10px_#E04E39]" 
                style={{
                  width: '100%',
                  animation: 'shimmer 1.5s infinite linear',
                  background: 'linear-gradient(90deg, transparent 0%, #E04E39 50%, transparent 100%)',
                  backgroundSize: '200% 100%'
                }}
              ></div>
            </div>
          </div>
        </div>
      )}
      <div className="border-b border-border pb-4">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-2">Grant Discovery</h1>
        <p className="text-base text-muted-foreground">Discover and explore funding opportunities</p>
      </div>

      <div className="bg-card border border-border rounded-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search grants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:border-[#E04E39]"
              />
            </div>
          </div>

          <select
            value={filters.sector}
            onChange={(e) => setFilters({...filters, sector: e.target.value})}
            className="px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:border-[#E04E39]"
          >
            <option value="">All Sectors</option>
            {sectors.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <select
            value={filters.geography}
            onChange={(e) => setFilters({...filters, geography: e.target.value})}
            className="px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:border-[#E04E39]"
          >
            <option value="">All Locations</option>
            {geographies.map(g => <option key={g} value={g}>{g}</option>)}
          </select>

          <button
            onClick={() => {
              setFilters({ sector: '', geography: '', funder_type: '', min_amount: '', max_amount: '' });
              setSearchTerm('');
            }}
            className="px-4 py-2 bg-background border border-border rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
          >
            Clear
          </button>

          <button
            onClick={handleAIDiscovery}
            className="px-4 py-2 bg-[#E04E39] text-white rounded-md flex items-center justify-center gap-2 hover:bg-[#E86B5A] transition-all shadow-lg"
          >
            <Sparkle size={18} weight="fill" />
            AI Discover
          </button>
        </div>
      </div>

      <div className="text-sm text-muted-foreground">
        {loading && grants.length === 0 ? 'Discovering opportunities...' : `Showing ${grants.length} of ${totalGrants} grants`}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {loading && grants.length === 0 ? (
          Array(6).fill(0).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          <>
            {grants.map((grant) => (
              <div 
                key={grant.id} 
                className="bg-card border border-border rounded-md p-6 hover:-translate-y-1 hover:border-border/50 hover:shadow-lg transition-all h-full flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="px-2 py-1 bg-[#E04E39]/15 text-[#E04E39] text-xs font-medium rounded uppercase tracking-wider">
                    {grant.funder_type}
                  </span>
                </div>

                <h3 className="text-lg sm:text-xl font-medium mb-2">{grant.name}</h3>
                <p className="text-sm text-muted-foreground mb-2">{grant.funder}</p>

                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <MapPin size={16} />
                  <span>{grant.geography}</span>
                  <span>•</span>
                  <span>{grant.sector}</span>
                </div>

                <p className="text-sm text-muted-foreground mb-4 line-clamp-2 flex-1">{grant.description}</p>

                <div className="mb-4">
                  <p className="text-2xl font-bold">${grant.amount.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Deadline: {new Date(grant.deadline).toLocaleDateString()}</p>
                </div>

                <div className="mt-auto">
                  <button
                    onClick={() => window.open('https://vanco.ai', '_blank')}
                    className="w-full px-4 py-2 bg-[#E04E39] hover:bg-[#E86B5A] text-white text-sm font-medium rounded-md transition-colors shadow-md"
                  >
                    Apply
                  </button>
                </div>
              </div>
            ))}
            {hasMore && (
              <div className="col-span-full py-8 flex justify-center">
                <button 
                  onClick={loadMore}
                  disabled={loading}
                  className="px-8 py-3 bg-card border border-border rounded-full text-sm text-muted-foreground hover:text-foreground hover:border-border/50 transition-all shadow-md"
                >
                  {loading ? 'Loading...' : 'Load More Results'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GrantDiscovery;
